/**
 * Reliability Service
 * Provides Standard Normal Deviate (Zr) values for pavement design reliability calculations
 *
 * Data source: ../data/reliability-zr-table.json (centralized configuration)
 * Standard source: AASHTO 1993 Guide for Design of Pavement Structures
 *
 * Used by:
 * - CalculationServiceFacade.ts (structural number calculations)
 * - MathUtilsService.ts (standard normal deviate calculations)
 *
 * Purpose: Eliminates hardcoded AASHTO standard table, ensures accuracy of reliability-based designs
 */

import reliabilityData from '../data/reliability-zr-table.json';

interface ReliabilityLevel {
    reliabilityPercent: number;
    zr: number;
    description?: string;
}

/**
 * Reliability lookup map
 * Loaded from centralized JSON configuration (../data/reliability-zr-table.json)
 * Values based on AASHTO 1993 Standard Normal Distribution Table
 */
const RELIABILITY_LEVELS: Map<number, number> = new Map(
    reliabilityData.reliabilityLevels.map((level: ReliabilityLevel) => [level.reliabilityPercent, level.zr])
);

/**
 * Default reliability percentage when requested value not found
 * From AASHTO: 95% is most commonly used in pavement design
 */
const DEFAULT_RELIABILITY = reliabilityData.defaultReliability;

export class ReliabilityService {
    /**
     * Get Standard Normal Deviate (Zr) for a given reliability percentage
     *
     * Matches AASHTO 1993 Standard Normal Distribution Table:
     * - 50% → 0.0
     * - 60% → -0.253
     * - 70% → -0.524
     * - 75% → -0.674
     * - 80% → -0.841
     * - 85% → -1.037
     * - 90% → -1.282
     * - 95% → -1.6448 (most common default)
     * - 99% → -2.326
     * - 99.9% → -3.09
     *
     * @param reliabilityPercent - Design reliability percentage (0-100)
     * @returns Standard Normal Deviate (Zr) - typically negative
     *
     * @example
     * ```typescript
     * const zr = ReliabilityService.getZr(95); // Returns -1.6448
     * const zrDefault = ReliabilityService.getZr(87); // Returns -1.6448 (fallback to 95%)
     * ```
     */
    static getZr(reliabilityPercent: number): number {
        const zr = RELIABILITY_LEVELS.get(reliabilityPercent);

        if (zr !== undefined) {
            return zr;
        }

        // Fallback to default reliability (95% = -1.6448)
        const defaultZr = RELIABILITY_LEVELS.get(DEFAULT_RELIABILITY);

        if (defaultZr === undefined) {
            throw new Error(
                `Reliability data corrupted: default reliability ${DEFAULT_RELIABILITY}% not found. Check reliability-zr-table.json integrity.`
            );
        }

        return defaultZr;
    }

    /**
     * Get all available reliability percentages
     * Useful for validation and UI display
     *
     * @returns Array of available reliability percentages (e.g., [50, 60, 70, ...])
     */
    static getAvailableReliabilities(): number[] {
        return Array.from(RELIABILITY_LEVELS.keys()).sort((a, b) => a - b);
    }

    /**
     * Get complete reliability mapping
     * Useful for debugging and validation
     *
     * @returns Map of reliability % → Zr
     */
    static getAllReliabilityLevels(): Map<number, number> {
        return new Map(RELIABILITY_LEVELS);
    }

    /**
     * Validate if a reliability percentage is explicitly defined in the table
     *
     * @param reliabilityPercent - Reliability percentage to check
     * @returns true if reliability is in table, false if will use fallback
     */
    static isReliabilityDefined(reliabilityPercent: number): boolean {
        return RELIABILITY_LEVELS.has(reliabilityPercent);
    }

    /**
     * Get description for a reliability level
     * Useful for debugging and UI display
     *
     * @param reliabilityPercent - Reliability percentage
     * @returns Description or undefined if not available
     */
    static getDescription(reliabilityPercent: number): string | undefined {
        const level = reliabilityData.reliabilityLevels.find((l: ReliabilityLevel) => l.reliabilityPercent === reliabilityPercent);
        return level?.description;
    }

    /**
     * Check if reliability percentage is within valid range
     * AASHTO typically uses 50% to 99.9% range
     *
     * @param reliabilityPercent - Reliability percentage to validate
     * @returns true if within reasonable range (0-100)
     */
    static isValidReliability(reliabilityPercent: number): boolean {
        return reliabilityPercent >= 0 && reliabilityPercent <= 100;
    }
}
